/**
 * CirclingMinimaChip — Phase B text chip for circling minima on approach cards.
 *
 * Renders the circling radius, MDA, visibility, and any sector restrictions
 * for the active aircraft category at the selected IAP plate.
 *
 * Radius lookup uses the FAA AIM 5-4-20 Table 5-4-29 unified table, keyed by
 * circling MDA in feet MSL. FAA TERM 25-05 (May 14 2025) removed the inverse-C
 * "expanded circling" marker — this single table applies to all approaches.
 *
 * Safety contract:
 *   - Never claim "no restrictions" when cifp_minima_variations rows exist.
 *   - Always show which MDA-MSL band the radius came from so the pilot knows
 *     which row of the table was used.
 *   - Show ⚠ when parsed MDA data is missing for the active category.
 *   - Advisory-only; pilot must verify against the published plate.
 *
 * Usage:
 *   <CirclingMinimaChip
 *     airportIcao="KMRY"
 *     procIdent="I10R"
 *     airportElevFt={257}
 *   />
 */

import { useEffect, useState } from 'react';

import { fetchCirclingMinima, type CirclingMinimaApiResponse } from '../lib/plates';
import {
  computeCirclingRadius,
  type ApproachCategory,
} from '../lib/circlingRadius';
import { getEffectiveApproachCategory } from '../lib/aircraftClass';
import { formatRestrictionsForChip } from '../lib/circlingRestrictions';
import { useAircraftStore } from '../stores/aircraftStore';
import { usePlanStore } from '../stores/planStore';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
  /** 4-letter ICAO airport identifier, e.g. "KMRY". */
  airportIcao: string;
  /**
   * Procedure identifier derived from the chart name via deriveProcIdent(),
   * e.g. "I10R", "R28L", "RY09".
   */
  procIdent: string;
  /**
   * Airport field elevation (MSL ft) from NASR data. Used only to compute
   * the AGL display value beside the MDA — does NOT affect the radius lookup.
   * Pass 0 as a safe fallback when NASR data hasn't loaded yet.
   */
  airportElevFt: number;
}

// ─── Category MDA resolver ────────────────────────────────────────────────────

function getMdaForCategory(
  minima: NonNullable<CirclingMinimaApiResponse['minima']>,
  category: ApproachCategory,
): number | null {
  switch (category) {
    case 'A': return minima.circling_mda_a;
    case 'B': return minima.circling_mda_b;
    case 'C': return minima.circling_mda_c;
    case 'D': return minima.circling_mda_d;
    case 'E': return minima.circling_mda_e;
  }
}

function getVisForCategory(
  minima: NonNullable<CirclingMinimaApiResponse['minima']>,
  category: ApproachCategory,
): string | null {
  switch (category) {
    case 'A': return minima.circling_vis_a;
    case 'B': return minima.circling_vis_b;
    case 'C': return minima.circling_vis_c;
    case 'D': return minima.circling_vis_d;
    case 'E': return minima.circling_vis_e;
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CirclingMinimaChip({ airportIcao, procIdent, airportElevFt }: Props) {
  const [data, setData] = useState<CirclingMinimaApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Resolve active aircraft category
  const aircraftList = useAircraftStore((s) => s.list);
  const aircraftId = usePlanStore((s) => s.aircraftId);
  const activeAircraft = aircraftList.find((a) => a.id === aircraftId) ?? null;

  // Derive approach category from the active aircraft
  // Approach speed is not modeled in the v2 profile's VSpeeds; fall back
  // to class-based default. CAT A is used when no aircraft is selected
  // (provides a conservative reference preview).
  const category: ApproachCategory = activeAircraft
    ? getEffectiveApproachCategory(activeAircraft.icaoType)
    : 'A';

  // Fetch circling minima for this plate
  useEffect(() => {
    setLoading(true);
    setError(null);
    const ac = new AbortController();
    fetchCirclingMinima(airportIcao, procIdent, ac.signal)
      .then((result) => {
        setData(result);
        setLoading(false);
      })
      .catch((e) => {
        if ((e as Error)?.name !== 'AbortError') {
          setError((e as Error)?.message ?? 'fetch failed');
        }
        setLoading(false);
      });
    return () => ac.abort();
  }, [airportIcao, procIdent]);

  if (loading) {
    return (
      <div className="mt-2 border border-cockpit-700 rounded-md bg-cockpit-800/50 px-3 py-2 text-xs text-cockpit-400 animate-pulse">
        Loading circling minima…
      </div>
    );
  }

  if (error || !data) {
    return null; // Silently suppress — circling data is supplemental
  }

  const { minima, variations } = data;

  // Filter bare "CIRCLING" text from variations (edge-case fix #3)
  const filteredVariations = variations.filter(
    (v) => v.raw_text.trim().toUpperCase() !== 'CIRCLING' && v.raw_text.trim().length > 10,
  );

  if (!minima && filteredVariations.length === 0) {
    // No circling data in DB for this plate — don't show the chip
    return null;
  }

  const mdaFt = minima ? getMdaForCategory(minima, category) : null;
  const visStr = minima ? getVisForCategory(minima, category) : null;

  // Compute radius (unified MDA-MSL table)
  const radiusResult = computeCirclingRadius({
    category,
    circlingMdaFt: mdaFt,
    airportElevFt,
  });

  const mdaMissing = minima && mdaFt === null;

  // Restriction summary
  const restrictionsText = filteredVariations.length > 0
    ? formatRestrictionsForChip(filteredVariations as Parameters<typeof formatRestrictionsForChip>[0])
    : null;

  // AGL calculation for display
  const mdaAglFt = mdaFt != null ? mdaFt - airportElevFt : null;

  return (
    <div className="mt-2 border border-cockpit-700 rounded-md bg-cockpit-800/50 text-xs overflow-hidden">
      {/* Header row */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-cockpit-700 bg-cockpit-700/20">
        <span className="text-[10px] font-semibold tracking-wider text-cockpit-300 uppercase">
          Circling Minima
        </span>
        <div className="flex items-center gap-2">
          {/* Active aircraft category badge */}
          <span className="text-[10px] bg-ember-600/30 text-ember-300 border border-ember-600/40 rounded px-1.5 py-0.5 font-mono">
            CAT {category}
            {activeAircraft ? (
              <span className="text-ember-400 ml-1 text-[9px]">({activeAircraft.tail || activeAircraft.typeLabel || 'your aircraft'})</span>
            ) : (
              <span className="text-cockpit-400 ml-1 text-[9px]">(no aircraft selected)</span>
            )}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="px-3 py-2 space-y-1">
        {/* Radius row */}
        <div className="flex items-baseline justify-between">
          <span className="text-cockpit-300">Radius</span>
          <span className="font-mono text-cockpit-100">
            {radiusResult.radiusNm.toFixed(1)} NM
            <span className="text-[10px] text-cockpit-400 ml-1">
              {radiusResult.isFallback
                ? `(using ${radiusResult.bandLabel} band — MDA not parsed)`
                : `(${radiusResult.bandLabel} band)`}
            </span>
          </span>
        </div>

        {/* MDA row — show warning when null for active category */}
        {mdaMissing ? (
          <div className="flex items-center gap-1 text-amber-300">
            <span>⚠</span>
            <span>CAT {category} MDA not parsed for this plate</span>
          </div>
        ) : mdaFt != null ? (
          <div className="flex items-baseline justify-between">
            <span className="text-cockpit-300">MDA</span>
            <span className="font-mono text-cockpit-100">
              {mdaFt.toLocaleString()} ft MSL
              {mdaAglFt != null && (
                <span className="text-[10px] text-cockpit-400 ml-1">
                  ({mdaAglFt > 0 ? `${mdaAglFt.toLocaleString()} ft AGL` : 'at field elevation'} above {airportIcao})
                </span>
              )}
            </span>
          </div>
        ) : null}

        {/* Visibility */}
        {visStr && (
          <div className="flex items-baseline justify-between">
            <span className="text-cockpit-300">Visibility</span>
            <span className="font-mono text-cockpit-100">{visStr}</span>
          </div>
        )}

        {/* Restrictions */}
        {restrictionsText && (
          <div className="pt-1 border-t border-cockpit-700/50">
            <span className="text-[10px] tracking-wider text-amber-300 uppercase font-medium">
              Restrictions:
            </span>
            <div className="text-cockpit-100 mt-0.5 leading-snug">
              {restrictionsText}
            </div>
          </div>
        )}

        {/* Advisory disclaimer */}
        <div className="pt-1 text-[9px] text-cockpit-500 italic">
          Advisory only — verify against published approach plate
        </div>
      </div>
    </div>
  );
}
